import { useState } from "react";
import type { StoreApi } from "zustand";
import { useTranslation } from "react-i18next";
import {
  useTenant,
  type AppFeatureDefinition,
  type AuthController,
  type AuthState,
  type FeatureNavNode,
} from "@nasam/core";
import { cn, LoginLinearIcon, LogoutLinearIcon } from "@nasam/ui";
import { resolveIcon } from "./iconRegistry";
import { LogoutConfirmDialog } from "./LogoutConfirmDialog";
import { UserMenu } from "./UserMenu";

export interface NavRailProps {
  readonly railNodes: readonly FeatureNavNode[];
  readonly moduleDefinitions?:
    ReadonlyMap<string, AppFeatureDefinition> | undefined;
  readonly activeRailItemId: string | null;
  readonly onSelectRailItem: (node: FeatureNavNode) => void;
  readonly authController?: AuthController | undefined;
  readonly authStore?: StoreApi<AuthState> | undefined;
}

export function NavRail({
  railNodes,
  moduleDefinitions,
  activeRailItemId,
  onSelectRailItem,
  authController,
  authStore,
}: NavRailProps) {
  const { t, i18n } = useTranslation();
  const { branding } = useTenant();
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  // A CSS mirror/rotation had no visible effect on this icon (Solar's
  // logout glyph turned out symmetric under both), so direction is handled
  // by swapping the actual glyph instead. The plain "logout" glyph's own
  // drawn direction is what LTR needs pointed the *other* way, so LTR gets
  // the pre-mirrored "login" glyph and RTL gets "logout" as originally drawn.
  const LogoutIcon = i18n.dir() === "rtl" ? LogoutLinearIcon : LoginLinearIcon;
  // An `account` node isn't an icon in the strip: the avatar opens it.
  const iconNodes = railNodes.filter((node) => node.placement !== "account");
  const accountNode = railNodes.find((node) => node.placement === "account");

  return (
    <aside
      aria-label="Navigation rail"
      data-node-id="881:39086"
      className="z-30 hidden h-full min-h-0 w-[4.6875rem] shrink-0 flex-col items-center overflow-hidden rounded-xl bg-blue-purple-700 px-1 py-4 select-none md:flex"
    >
      <div className="flex min-h-0 w-full flex-1 flex-col items-center gap-6">
        <img
          data-testid="shell-brand-logo"
          src={branding?.logoUrl ?? "/brand/logo-mark.png"}
          alt=""
          className="size-14 shrink-0 object-contain"
        />

        <nav
          aria-label="Primary navigation"
          className="scrollbar-hidden flex min-h-0 w-full flex-1 flex-col items-center gap-4 overflow-y-auto overscroll-contain"
        >
          {iconNodes.map((node) => {
            const isActive = activeRailItemId === node.id;
            const featureDef = moduleDefinitions?.get(node.id);
            const NavItemOverride = featureDef?.navItemComponent;
            const NodeOverride = node.component;

            if (NavItemOverride !== undefined) {
              return <NavItemOverride key={node.id} />;
            }

            if (NodeOverride !== undefined) {
              return (
                <NodeOverride key={node.id} node={node} isActive={isActive} />
              );
            }

            const title = t(
              node.titleKey.includes(":")
                ? node.titleKey
                : `navigation:${node.titleKey}`,
            );
            const Icon = resolveIcon(node.icon);

            return (
              <button
                key={node.id}
                type="button"
                data-testid={`rail-item-${node.id}`}
                data-active={isActive ? "true" : "false"}
                onClick={() => onSelectRailItem(node)}
                className="flex w-full flex-col items-center rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-white"
              >
                <span
                  className={cn(
                    "flex items-center justify-center rounded-md p-2 transition-colors duration-150 ease-emphasized",
                    isActive
                      ? "bg-neutral-white text-blue-purple-700"
                      : "text-neutral-white",
                  )}
                >
                  <Icon className="size-6 shrink-0" />
                </span>
                {/* Figma caps rail items at 62px and already wraps them to
                    two lines (881:39049), but the label was unbounded, so
                    long English titles spilled outside the rail. The soft
                    hyphens in the en `businessModules` copy pick the break;
                    `break-words` is the fallback for any future label with
                    neither a space nor one. */}
                <span className="w-full break-words text-center text-body-caption-m-bold leading-normal text-neutral-white">
                  {title}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex w-full shrink-0 flex-col items-center gap-[0.9375rem] border-t border-white/30 pt-4">
        <button
          type="button"
          data-testid="rail-logout-button"
          aria-label={t("common:logout")}
          onClick={() => setConfirmingLogout(true)}
          className="flex size-9 items-center justify-center rounded-md text-neutral-white transition-colors duration-150 ease-emphasized hover:bg-white/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-white"
        >
          <LogoutIcon className="size-6" />
        </button>
        <UserMenu
          {...(authController !== undefined ? { authController } : {})}
          {...(authStore !== undefined ? { authStore } : {})}
          accountNode={accountNode}
          accountActive={
            accountNode !== undefined && activeRailItemId === accountNode.id
          }
          onSelectAccountNode={onSelectRailItem}
        />
      </div>

      <LogoutConfirmDialog
        open={confirmingLogout}
        onOpenChange={setConfirmingLogout}
        {...(authController !== undefined ? { authController } : {})}
      />
    </aside>
  );
}
